import { useState, useCallback, useRef } from "react";
import { estateExploreService } from "../../../services/estateExplore/estateExploreService";
import legacy from "../../../utils/legacy";

const LIMIT = 20;

/**
 * 매물탐색 데이터(목록/상세) 관리 hook
 *
 * @param {Object} opts
 * @param {React.MutableRefObject} opts.payloadRef  — 최신 필터 payload ref
 * @param {React.MutableRefObject} opts.sideviewRef — sideview DOM ref (리스트 갱신 시 닫기용)
 * @param {"desktop"|"mobile"|"tablet"} opts.view   — PC는 getList2, 모바일은 getList2Mobile
 */
export default function useExploreData({ payloadRef, sideviewRef, view }) {
  const [data, setData] = useState({ list: [], count: 0, totalCount: 0 });
  const [detailData, setDetailData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const fetchGenRef = useRef(0);
  const listRef = useRef(null);
  // 맵 바운드 파라미터 캐시 (바텀시트 더보기 등에서 재사용)
  const extraParamsRef = useRef({});
  // 마지막 요청 payload 캐시 (loadMore에서 conditions 등 유지)
  const lastPayloadRef = useRef(null);

  // v2 API로 통합 (PC/Mobile 구분은 platform 파라미터로)
  const fetchListApi = estateExploreService.getListV2;
  const fetchDetailApi = estateExploreService.getDetailV2;

  /**
   * 매물 목록 조회
   */
  const fetchList = useCallback(
    async (requestPayload) => {
      const gen = ++fetchGenRef.current;
      setIsLoading(true);

      try {
        // 요청 payload 캐시 (loadMore에서 재사용)
        lastPayloadRef.current = requestPayload;

        if (requestPayload.offset === 0) {
          // 리스트 갱신 시 sideview 닫기
          if (sideviewRef?.current) {
            sideviewRef.current.dispatchEvent(new CustomEvent("close"));
          }
          setHasMore(true);
          setData({ list: [], count: 0, totalCount: null });
        }

        const result = await fetchListApi(requestPayload);

        // stale 응답 무시
        if (gen !== fetchGenRef.current) return;

        // 줌 부족 — 리스트 비우기
        if (result.zoomRequired) {
          setData({ list: [], count: 0, totalCount: 0, zoomRequired: true });
          setHasMore(false);
          return;
        }

        // 단일/겹침 마커 클릭이면 동일매물 자동 펼침
        // group 타입 또는 _merged(프론트 겹침)만 해당, region/bounds 클러스터는 제외
        const isGroupSelect = (() => {
          try {
            const conds = JSON.parse(requestPayload.conditions || "[]");
            if (!Array.isArray(conds) || conds.length === 0) return false;
            return conds.every((c) => c.type === "group");
          } catch { return false; }
        })();

        if (requestPayload.offset === 0) {
          listRef.current?.scrollToTop?.();
          setData({
            list: result.data,
            count: result.count,
            totalCount: result.totalCount,
            autoExpand: isGroupSelect,
          });
        } else {
          setData((prev) => ({
            list: [...prev.list, ...result.data],
            count: result.count,
            totalCount: result.totalCount,
            autoExpand: prev.autoExpand,
          }));
        }

        // hasMore 판정: 모바일은 result.hasNext, PC는 count < limit
        if (result.hasNext === false || result.count < requestPayload.limit) {
          setHasMore(false);
        }
      } catch (err) {
        // 매물 조회 오류
      } finally {
        if (gen === fetchGenRef.current) {
          setIsLoading(false);
        }
      }
    },
    [fetchListApi, sideviewRef]
  );

  /**
   * 현재 맵 바운드 + 필터로 목록 로드
   */
  const loadData = useCallback(
    (filterPayload, customOffset = 0) => {
      const bounds = legacy.discoMap.getBounds();
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const center = legacy.discoMap.getSltCenter();
      const map_lv = legacy.map.getLevel();

      const mapParams = {
        swLat: sw.getLat(),
        swLng: sw.getLng(),
        neLat: ne.getLat(),
        neLng: ne.getLng(),
        map_lv,
        centerLat: center.getLat(),
        centerLng: center.getLng(),
      };
      extraParamsRef.current = mapParams;

      fetchList({
        ...filterPayload,
        offset: customOffset,
        limit: LIMIT,
        ...mapParams,
      });
    },
    [fetchList]
  );

  /**
   * offset 초기화 후 새로 로드 (새로고침 버튼 등)
   */
  const requestLoadData = useCallback(() => {
    setOffset(0);
    loadData(payloadRef.current, 0);
  }, [loadData, payloadRef]);

  /**
   * 무한 스크롤 다음 페이지 — 마지막 요청 payload를 재사용하여
   * conditions 등 컨텍스트 유지
   */
  const loadMore = useCallback(() => {
    const nextOffset = offset + LIMIT;
    setOffset(nextOffset);

    if (lastPayloadRef.current) {
      fetchList({ ...lastPayloadRef.current, offset: nextOffset });
    } else {
      loadData(payloadRef.current, nextOffset);
    }
  }, [offset, fetchList, loadData, payloadRef]);

  /**
   * 그룹 상세 목록 (같은 주소 매물 펼치기)
   */
  const fetchDetailList = useCallback(
    async (groupKey) => {
      try {
        const detailPayload = {
          ...payloadRef.current,
          ...extraParamsRef.current,
          groupKey,
        };
        const result = await fetchDetailApi(detailPayload);
        setDetailData((prev) => ({
          ...prev,
          [groupKey]: result,
        }));
        return result;
      } catch (err) {
        return null;
      }
    },
    [fetchDetailApi, payloadRef]
  );

  return {
    data,
    setData,
    detailData,
    setDetailData,
    isLoading,
    hasMore,
    offset,
    setOffset,
    listRef,
    extraParamsRef,
    fetchList,
    loadData,
    requestLoadData,
    loadMore,
    fetchDetailList,
    LIMIT,
  };
}
